import { JsonApiRequestError, Mutation } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import { Bonus, createEditBonusMutation, EditBonusMutationParams } from 'shared/api';
import {
	createModalFormModel,
	greaterZeroRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type EditBonusFormValue = {
	activationThreshold: string;
	depositRate: string;
};

export type EditBonusModel = ModalFormModel<Bonus, EditBonusFormValue> & {
	editBonusMutation: Mutation<EditBonusMutationParams, Bonus, JsonApiRequestError>;
	$pending: Store<boolean>;
};

export const createEditBonusModel = (): EditBonusModel => {
	const editBonusMutation = createEditBonusMutation();

	const formModel = createModalFormModel<Bonus, EditBonusFormValue>({
		fields: {
			activationThreshold: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
			depositRate: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	sample({
		clock: formModel.modalButtonClicked,
		fn: (bonus) => ({
			activationThreshold: bonus.activationThreshold.toString(),
			depositRate: (bonus.depositRate * 100).toFixed(2),
		}),
		target: [formModel.form.setInitialValues, formModel.form.setValues],
	});

	sample({
		clock: formModel.form.submitted,
		source: { form: formModel.form.$values, bonus: formModel.$modalData },
		filter: ({ bonus }) => bonus !== null,
		fn: ({ form, bonus }) => ({
			id: bonus!.id,
			activationThreshold: Number(form.activationThreshold),
			depositRate: Number(form.depositRate) / 100,
		}),
		target: editBonusMutation.start,
	});

	notify(editBonusMutation.finished.success, 'edit-bonus.success');

	chain(editBonusMutation.finished.success, formModel.switcher.disable);

	sample({
		clock: formModel.modalClosed,
		fn: () => ({
			activationThreshold: '',
			depositRate: '',
		}),
		target: [formModel.form.setInitialValues, formModel.form.setValues],
	});

	return {
		editBonusMutation,
		$pending: editBonusMutation.$pending,
		...formModel,
	};
};
